import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import { useLazyQuery, useApolloClient } from '@apollo/client';
import { useRootStore } from 'store';
import { Card, Row } from 'react-bootstrap';
import {
  CellFloatCustom,
  CellLeft,
  FlexRow,
  FormFilterCard,
  TableInMem,
  YupStringReq,
  YupNumericReq,
  YupNumeric,
  exportXlsModal,
  FormCol4,
} from 'motion-components';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import * as Yup from 'yup';
import { FieldSelectContributor } from 'shared-pages/components';

import gql from 'graphql-tag';
import { SelectContributionYear } from '../components/SelectContributionYear';
import { useTranslation } from 'react-i18next';

export const gqlGetSumOfEmpaContributionsItem = gql`
  query getSumOfEmpaContributionsItem($contributorId: Int, $year: Int) {
    getSumOfEmpaContributionsItem(contributorId: $contributorId, year: $year) {
      data {
        empaName
        qty1
        qty2
      }
      recordCount
    }
  }
`;

/* ----------- Page ------------------ */
export const YearlyEmpaContributionsPage = () => {
  const { appStore } = useRootStore();
  const client = useApolloClient();
  const { t } = useTranslation();

  /* ----------------- Route State -------------------------- */
  const location = useLocation<any>();
  const history = useHistory();
  // Στο state του routing αποθηκεύω:
  //    1. την τρέχουσα σελίδα του table pagination (pageIndex)
  //  & 2. τα values των φίλτρων (filterValues)

  /// Αρχικές Τιμές Στα Φίλτρα
  useEffect(() => {
    const values = location?.state?.filterValues ?? {};
    if (!values.year) {
      values.year = new Date().getFullYear();
      // Ενημέρωσε το State του Route
      history.replace(location.pathname, {
        ...location.state,
        filterValues: values,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [queryParams, setQueryParams] = useState({
    year: parseInt(location?.state?.filterValues.year) ?? 2030,
    contributorId: location?.state?.filterValues.contributorId ?? 999,
  });

  const [footerValues, setFooterValues] = useState({
    qty1: 0,
    qty2: 0,
  });

  const [getData, { loading, data: dataRaw }] = useLazyQuery(gqlGetSumOfEmpaContributionsItem, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      // Υπολόγισε αθροίσματα
      if (data.getSumOfEmpaContributionsItem?.data) {
        //console.log('$$$ onCompleted', data.getSumOfContributionsItem);
        let qty1 = 0;
        let qty2 = 0;
        for (let index = 0; index < data.getSumOfEmpaContributionsItem?.data.length; index++) {
          const values = data.getSumOfEmpaContributionsItem?.data[index];
          qty1 += values.qty1;
          qty2 += values.qty2;
        }
        setFooterValues({ qty1, qty2 });
      }
    },
  });
  const data = dataRaw?.getSumOfEmpaContributionsItem?.data;
  const recordCount = dataRaw?.getSumOfEmpaContributionsItem?.recordCount;

  const filtersChanged = useCallback(
    (values) => {
      console.log('filtersChanged', values);

      // Σύνθεσε τα vars του graphql
      const newQueryParams = {
        ...queryParams,
        year: parseInt(values.year),
        contributorId: parseInt(values.contributorId),
      };
      setQueryParams(newQueryParams); // αποθήκευσε τα vars
      // φερε τα δεδομένα απο τον σερβερ
      getData({ variables: { ...newQueryParams } });

      // Ενημέρωσε το State του Route
      //navigate(location.pathname, {
      //   replace: true,
      //   state: {
      //     filterValues: values,
      //     pageIndex: 0,
      //   },
      // });
    },
    [getData, queryParams],
  );

  function useExcelGetQuery() {
    return useLazyQuery(gqlGetSumOfEmpaContributionsItem);
    //return [null, { loading: null, data: null }];
  }
  const handleExport = useCallback(async () => {
    const filename = queryParams.contributorId
      ? `Ετήσιες Ποσότητες ΕΜΠΑ  ${queryParams.year} ${queryParams.contributorId}`
      : `Ετήσιες Ποσότητες ΕΜΠΑ ${queryParams.year}`;
    await exportXlsModal({
      apolloClient: client,
      tableName: undefined,
      getLazyQuery: useExcelGetQuery,
      filterVariables: queryParams,
      exportFileName: filename,
      columnsDef: [
        { fieldName: 'empaName', fieldTitle: 'Κατηγορία', fieldType: 'TEXT' },
        { fieldName: 'qty1', fieldTitle: 'Ποσότητα (TMX)', fieldType: 'FLOAT' },
        { fieldName: 'qty2', fieldTitle: 'Ποσότητα (Kg)', fieldType: 'FLOAT' },
      ],
    });
  }, [queryParams]);

  const columns = useMemo(
    () => [
      {
        accessor: 'empaName',
        Header: t('Κατηγορία'),
        Cell: CellLeft,
      },
      {
        accessor: 'qty1',
        Header: t('Ποσότητα (TMX)'),
        Cell: CellFloatCustom(5, 2),
        Footer: () => {
          return CellFloatCustom(5, 2)({ cell: { value: footerValues.qty1 } });
        },
      },
      {
        accessor: 'qty2',
        Header: t('Ποσότητα (Kg)'),
        Cell: CellFloatCustom(5, 2),
        Footer: () => {
          return CellFloatCustom(5, 2)({ cell: { value: footerValues.qty2 } });
        },
      },
    ],
    [footerValues.qty1, footerValues.qty2],
  );
  return (
    <Card className="shadow">
      <Card.Header>
        <FlexRow>
          <Card.Title className="m-0">{t('Ετήσια Αναφορά ΕΜΠΑ')}</Card.Title>
        </FlexRow>
      </Card.Header>
      <Card.Body className="nopadding">
        {/* Φίλτρα */}
        <Filters onChange={filtersChanged} initialValues={location?.state?.filterValues ?? {}} />
        {/* TableGrid */}
        <LoadingOverlay active={loading} spinner text="Παρακαλώ περιμένετε...">
          {data ? (
            <TableInMem
              //onRefresh={() => refetch()}
              onExport={handleExport}
              columns={columns}
              data={data}
              hasSearch={false}
              hasFooter={true}
              propPageSize={50}
            />
          ) : (
            <div className="empty-table-container"></div>
          )}
        </LoadingOverlay>
      </Card.Body>
    </Card>
  );
};

/* ----------- Filters ------------------ */
const Filters = ({ onChange, initialValues }) => {
  //
  const { appStore } = useRootStore();
  const { t } = useTranslation();
  function handleFiltersSubmit(values, { setSubmitting }) {
    setTimeout(() => {
      setSubmitting(false);
      onChange(values);
    }, 400);
  }
  console.log('filters-init ', initialValues);

  const filters = {
    contributorId: initialValues?.contributorId ?? '',
    year: initialValues?.year ?? '',
  };

  //const { values } = useFormikContext();
  const formShape = {
    contributorId: appStore.isContributor ? YupNumeric : YupNumericReq,
    year: YupStringReq,
  };

  const validationSchema = Yup.object().shape(formShape);
  return (
    <FormFilterCard
      title={t('Κριτήρια Αναζήτησης')}
      onSubmit={handleFiltersSubmit}
      initialValues={{
        contributorId: filters?.contributorId ?? '',
        year: filters?.year,
      }}
      validationSchema={validationSchema}
    >
      <Row>
        {appStore.isPlatformOwner && <FieldSelectContributor as={FormCol4} required={true} />}
        <SelectContributionYear required={true} />
      </Row>
    </FormFilterCard>
  );
};
